/**
 * The request fields an invocation block produces, shared by every form that
 * invokes an action.
 *
 * A trigger target maps `invocationInput` to `invocation_input`; the manual
 * invoke form maps it to `rich_input_data`. Everything else goes out under the
 * name it has here, which is the name both wire shapes use.
 */

import {
  UploadDestinationType,
  type ComputeRequirements,
  type ContainerParameters,
  type FileSelector,
  type InvocationInput,
  type InvocationUploadDestination,
} from "@/shared/domain/actions";
import { QueryTarget } from "@/shared/domain/query";

import {
  parseComputeRequirements,
  parseContainerParameters,
  parseTimeout,
  transformCommand,
} from "../components/action/actionUtils";

import {
  TRIGGERING_DATASET_ID,
  type InvocationSpecOutput,
  type InputSourceValues,
} from "./invocationSpecSchema";

/** Drops the rows an author added and never filled in. */
export function cleanPatterns(patterns: readonly string[]): string[] {
  return patterns.filter((pattern) => pattern.trim().length > 0);
}

/**
 * The action a request names: `ActionReference` as a request stores it, with
 * explicit nulls for "the invoking org" and "the latest version".
 */
export interface InvocationActionReference {
  name: string;
  owner: string | null;
  digest: string | null;
}

/** The fields an invoke request carries however the form around it is shaped. */
export interface InvocationSpecRequestFields {
  action: InvocationActionReference;
  /** The input rows as selectors, or null when nothing was added. */
  invocationInput: InvocationInput | null;
  parameter_values: Record<string, unknown>;
  compute_requirement_overrides: ComputeRequirements | null;
  container_parameter_overrides: ContainerParameters | null;
  timeout: number | null;
  /** Null when outputs go wherever the runtime sends them by default. */
  upload_destination: InvocationUploadDestination | null;
}

/**
 * The `{ target, query }` pair a stored invocation input spells, or
 * `undefined` when it names its inputs some other way. The selectors are read
 * as their first entry.
 */
export function storedInvocationQuery(
  input: InvocationInput | null | undefined,
): { target: QueryTarget; query: string } | undefined {
  if (input === null || input === undefined) {
    return undefined;
  }
  const topics = Array.isArray(input.topics) ? input.topics[0] : input.topics;
  if (topics?.query) {
    return { target: QueryTarget.Topics, query: topics.query };
  }
  const files = Array.isArray(input.files) ? input.files[0] : input.files;
  if (files?.query) {
    return { target: QueryTarget.Files, query: files.query };
  }
  return undefined;
}

/** Environment-variable rows as the wire's record; rows with no name are dropped. */
function envVarsOf(
  rows: InvocationSpecOutput["overrides"]["envVars"],
): Record<string, string> | undefined {
  const named = rows.filter((row) => row.key.trim().length > 0);
  if (named.length === 0) {
    return undefined;
  }
  return Object.fromEntries(named.map((row) => [row.key.trim(), row.value]));
}

function selectorOf(row: InputSourceValues): FileSelector {
  const empty = { query: null, ids: null, names: null, dataset_id: null };
  if (row.source === "query") {
    return { ...empty, paths: null, query: row.text.trim() };
  }
  if (row.source === "triggering-dataset") {
    // Named by a placeholder the runtime resolves against the firing, so the
    // selector reads whichever dataset the event was about.
    return {
      ...empty,
      dataset_id: TRIGGERING_DATASET_ID,
      paths: row.globs.length > 0 ? [...row.globs] : null,
    };
  }
  return {
    ...empty,
    // A dataset row is one selector however many patterns it carries, since
    // `paths` is a list; two rows would ask for two separate mounts.
    dataset_id: row.datasetId,
    paths: row.globs.length > 0 ? [...row.globs] : null,
  };
}

/**
 * The input rows as one invocation input, or null when there are none: the
 * field is omitted rather than sent empty, so a round-tripped target equals
 * the stored one.
 */
export function invocationInputOf(
  rows: readonly InputSourceValues[],
): InvocationInput | null {
  if (rows.length === 0) {
    return null;
  }
  const selectors = rows.map(selectorOf);
  return {
    files: selectors.length === 1 ? selectors[0] : selectors,
    topics: null,
  };
}

/**
 * The container overrides the wire carries. The shared parser sends the entry
 * point as one element whatever it holds, so it is split here the way the
 * command is: a stored entry point with arguments saves back whole.
 */
function containerOverridesOf(
  overrides: InvocationSpecOutput["overrides"],
): ContainerParameters | null {
  const container = parseContainerParameters(
    overrides.command,
    overrides.entrypoint,
    overrides.workdir,
    envVarsOf(overrides.envVars),
  );
  if (container === null || overrides.entrypoint === undefined) {
    return container;
  }
  return {
    ...container,
    entry_point: transformCommand(overrides.entrypoint, false),
  };
}

/** Maps an invocation block to the request fields it produces. */
export function buildInvocationSpec(
  values: InvocationSpecOutput,
): InvocationSpecRequestFields {
  const { overrides, outputDestination } = values;
  return {
    action: {
      name: values.action.name,
      owner: values.action.owner,
      digest: values.action.digest,
    },
    invocationInput: invocationInputOf(values.inputs),
    // A parameter cleared to nothing is left out, so its default applies.
    parameter_values: Object.fromEntries(
      Object.entries(values.parameters).filter(
        ([, value]) => !(typeof value === "string" && value.trim() === ""),
      ),
    ),
    compute_requirement_overrides: parseComputeRequirements(
      overrides.cpu,
      overrides.memory,
      overrides.gpu,
      overrides.storage,
    ),
    container_parameter_overrides: containerOverridesOf(overrides),
    timeout:
      overrides.timeout === null || overrides.timeout === undefined
        ? null
        : parseTimeout(overrides.timeout),
    upload_destination:
      outputDestination.mode === "dataset"
        ? {
            destination_type: UploadDestinationType.Dataset,
            destination_id: outputDestination.datasetId,
          }
        : null,
  };
}
